import React, { useEffect, useRef, useState } from 'react'
import { AuthLayout } from '../AuthLayout'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from '../../../../../hooks/useForm'
import { resendVerificationEmail, verifyEmail } from '../../../../../helpers/auth-helper'
import { updateEmailVerified } from '../../../../../actions/company'

export const VerifyEmail = () => {

    const { email, token } = useSelector(state => state.auth);
    const dispatch = useDispatch();
    
    const [formValues, handleInputChange] = useForm({
        digit1: '',
        digit2: '',
        digit3: '',
        digit4: '',
        digit5: '',
        digit6: ''
    });

    const { digit1, digit2, digit3, digit4, digit5, digit6 } = formValues;

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendCount, setResendCount] = useState(0);

    // Referencias para cada input
    const input1Ref = useRef(null);
    const input2Ref = useRef(null);
    const input3Ref = useRef(null);
    const input4Ref = useRef(null);
    const input5Ref = useRef(null);
    const input6Ref = useRef(null);

    // Auto-focus al siguiente input
    const handleDigitChange = (e, nextRef) => {
        handleInputChange(e);
        if (e.target.value.length === 1 && nextRef) {
            nextRef.current.focus();
        }
    };

    // Manejar backspace para ir al input anterior
    const handleKeyDown = (e, prevRef) => {
        if (e.key === 'Backspace' && e.target.value === '' && prevRef) {
            prevRef.current.focus();
        }
    };

    const handleVerifyEmail = async (e) => {
        e.preventDefault();
        
        // Validar que todos los dígitos estén completos
        const code = `${digit1}${digit2}${digit3}${digit4}${digit5}${digit6}`;
        
        if (code.length !== 6) {
            setError('Por favor, ingresa el código completo de 6 dígitos');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const resp = await verifyEmail(token, code);
            
            if (resp.success) {
                // Actualizar el estado de Redux
                dispatch(updateEmailVerified(true));
            } else {
                setError(resp.message || 'Código inválido o expirado');
            }
        } catch (error) {
            setError(error?.response?.data?.message || 'Error al verificar el código. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerificationEmail = async () => {
        setError('');
        setLoading(true);
        setResendCount(resendCount + 1);

        try {
            const resp = await resendVerificationEmail(token);
            
            if (resp.success) {
                setError('Se ha enviado un nuevo código de verificación a tu correo.');
            } else {
                setError(resp.message || 'No se pudo reenviar el código. Intenta nuevamente.');
            }
        } catch (error) {
            setError('Error al reenviar el código. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        // Auto-focus al primer input al cargar el componente
        if (input1Ref.current) {
            input1Ref.current.focus();
        }
    }, []);

    return (
        <AuthLayout>
            <div className="card mb-4 mt-5 mx-4 mx-sm-0 position-relative">
                <div className="wd-50 bg-white p-2 rounded-circle shadow-lg position-absolute translate-middle top-0 start-50">
                    <img src="/assets/logo/logo-abbr.png" alt="" className="img-fluid" />
                </div>
                <div className="card-body p-sm-5">
                    <h2 className="fs-20 fw-bolder mb-4">Verifica tu correo electrónico</h2>
                    <h4 className="fs-13 fw-bold mb-2">Por favor, ingresa el código generado de una sola vez para verificar tu cuenta.</h4>
                    <p className="fs-12 fw-medium text-muted"><span>Se ha enviado un código a</span> <strong>{email}</strong></p>
                    
                    {error && (
                        <div className="alert alert-danger mt-3" role="alert">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleVerifyEmail} className="w-100 mt-4 pt-2">
                        <div id="otp" className="inputs d-flex flex-row justify-content-center mt-2">
                            <input 
                                ref={input1Ref}
                                className="m-2 text-center form-control rounded" 
                                type="text" 
                                name="digit1"
                                value={digit1}
                                onChange={(e) => handleDigitChange(e, input2Ref)}
                                maxLength="1" 
                                required 
                            />
                            <input 
                                ref={input2Ref}
                                className="m-2 text-center form-control rounded" 
                                type="text" 
                                name="digit2"
                                value={digit2}
                                onChange={(e) => handleDigitChange(e, input3Ref)}
                                onKeyDown={(e) => handleKeyDown(e, input1Ref)}
                                maxLength="1" 
                                required 
                            />
                            <input 
                                ref={input3Ref}
                                className="m-2 text-center form-control rounded" 
                                type="text" 
                                name="digit3"
                                value={digit3}
                                onChange={(e) => handleDigitChange(e, input4Ref)}
                                onKeyDown={(e) => handleKeyDown(e, input2Ref)}
                                maxLength="1" 
                                required 
                            />
                            <input 
                                ref={input4Ref}
                                className="m-2 text-center form-control rounded" 
                                type="text" 
                                name="digit4"
                                value={digit4}
                                onChange={(e) => handleDigitChange(e, input5Ref)}
                                onKeyDown={(e) => handleKeyDown(e, input3Ref)}
                                maxLength="1" 
                                required 
                            />
                            <input 
                                ref={input5Ref}
                                className="m-2 text-center form-control rounded" 
                                type="text" 
                                name="digit5"
                                value={digit5}
                                onChange={(e) => handleDigitChange(e, input6Ref)}
                                onKeyDown={(e) => handleKeyDown(e, input4Ref)}
                                maxLength="1" 
                                required 
                            />
                            <input 
                                ref={input6Ref}
                                className="m-2 text-center form-control rounded" 
                                type="text" 
                                name="digit6"
                                value={digit6}
                                onChange={handleInputChange}
                                onKeyDown={(e) => handleKeyDown(e, input5Ref)}
                                maxLength="1" 
                                required 
                            />
                        </div>
                        <div className="mt-5">
                            <button 
                                type="submit" 
                                className="btn btn-lg btn-primary w-100"
                                disabled={loading}
                            >
                                {loading ? 'Validando...' : 'Validar'}
                            </button>
                        </div>
                        <div className="mt-5 text-muted">
                            <span>¿No recibiste el código?</span> <button disabled={resendCount >= 3} className="btn btn-primary p-1" onClick={handleResendVerificationEmail}>Reenviar( {resendCount}/3 )</button>
                        </div>
                    </form>
                </div>
            </div>
        </AuthLayout>
    )
}
